<template>
  <div class="flex h-full min-h-0 flex-col py-2">
    <div ref="scrollRoot" class="flex-1 overflow-y-auto" role="list">
      <template v-if="loading && items.length === 0">
        <NotificationsSkeleton :count="8" />
      </template>

      <template v-else-if="error && items.length === 0">
        <Empty
          class="flex-1 px-8 py-12"
          icon="huge-warning"
          :title="$t('notifications.error.title')"
          :description="$t('notifications.error.description')"
        >
          <template #action>
            <Button variant="outline" size="sm" @click="$emit('retry')">
              {{ $t("notifications.error.retry") }}
            </Button>
          </template>
        </Empty>
      </template>

      <template v-else-if="isEmpty">
        <NotificationsEmpty />
      </template>

      <template v-else>
        <NotificationItem
          v-for="n in items"
          :key="n.uuid"
          :notification="n"
          @mark-read="$emit('mark-read', $event)"
        />

        <div v-if="hasMore" class="flex items-center justify-center px-4 py-3">
          <Button
            variant="outline"
            size="sm"
            class="gap-1.5"
            :disabled="loadingMore"
            @click="$emit('load-more')"
          >
            <Spinner v-if="loadingMore" class="h-3.5 w-3.5" />
            {{ $t("notifications.loadMore") }}
          </Button>
        </div>

        <div
          v-if="loading && items.length > 0"
          class="flex items-center justify-center gap-2 py-4 text-xs text-muted-foreground"
        >
          <Spinner class="h-3.5 w-3.5" />
          {{ $t("notifications.loading") }}
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref } from "vue";
import { Empty } from "@/shared/ui/empty";
import Button from "@/shared/ui/button/Button.vue";
import { Spinner } from "@/shared/ui/spinner";
import NotificationsSkeleton from "./NotificationsSkeleton.vue";
import NotificationsEmpty from "./NotificationsEmpty.vue";
import NotificationItem from "./NotificationItem.vue";

defineProps({
  items: { type: Array, required: true },
  loading: { type: Boolean, default: false },
  loadingMore: { type: Boolean, default: false },
  hasMore: { type: Boolean, default: false },
  isEmpty: { type: Boolean, default: false },
  error: { type: [Object, Error, null], default: null },
});

defineEmits(["mark-read", "retry", "load-more"]);

const scrollRoot = ref(null);
defineExpose({ scrollRoot });
</script>
